import React, { useEffect, useState } from 'react';
import { withStyles } from '@jpmuitk/theme';
import { ColumnLayout } from "@jpmuitk/column-layout";
import { Card } from "@jpmuitk/card";
import { Spinner } from "@jpmuitk/spinner";
import { AriaAnnouncerProvider } from "@jpmuitk/aria-announcer";
import { SVS, DOC, CONFIRMATION, LOADING } from './common/JwtConstants';
import SvsFlow from "./common/svs/SvsFlow";
import DocFlow from "./common/doc/DocFlow";
import { getDecodedSessionToken, getProfileComponents, getProfileAccountRegion } from './redux/clientActions';
import { useTranslation } from 'react-i18next';

const styles = {
  root: {
    background: '#F2F4F6',
    height: '100%',
    margin: '0 auto',
    overflow: 'auto',
  },
  panel: {
    flex: 1,
    overflow: 'auto',
  },
  mainContent: {
    flex: 1,
    width: '100%',
    height: '100%',
    margin: '0 auto',
    overflow: 'auto',
  },
  columnPanel: {
    textAlign: 'center',
    paddingTop: '5rem',
    paddingBottom: '5rem',
  },
};

const Landing = ({ classes }) => {
  const { t } = useTranslation();

  const { root, panel, mainContent } = classes;
  const [accessToken, setAccessToken] = useState("");
  const [sessionToken, setSessionToken] = useState("");
  const [successRedirectUrl, setSuccessRedirectUrl] = useState("");
  const [failureRedirectUrl, setFailureRedirectUrl] = useState("");
  const [initialRedirectUrl, setInitialRedirectUrl] = useState("");
  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileComponents, setProfileComponents] = useState([]);
  const [accountRegion, setAccountRegion] = useState("UNKNOWN");
  const [initialRenderedComponent, setInitialRenderedComponent] = useState(LOADING);
  const [profileComponentsLoaded, setProfileComponentsLoaded] = useState(false);
  const [flowType, setFlowType] = useState(null);

  useEffect(() => {
    const initialize = async () => {
      const urlParams = new URLSearchParams(document.location.search);
      let access_token = urlParams.get("accessToken");
      let session_token = urlParams.get("sessionToken");

      const response = await getDecodedSessionToken(access_token, session_token);

      if (response.status !== 200) {
        setLoading(false);
        setAuthorized(false);
        return;
      }

      const decodedToken = await response.json();

      if (decodedToken.type === SVS) {
        setFlowType(SVS);
        setAuthorized(true);
        setAccessToken(access_token);
        setSessionToken(session_token);
        setSuccessRedirectUrl(decodedToken.successUrl);
        setFailureRedirectUrl(decodedToken.failureUrl);
        try {
          const components = await getProfileComponents(access_token, session_token);
          setProfileComponents(components);
          try {
            const accountRegionResponse = await getProfileAccountRegion(access_token, session_token);
            setAccountRegion(accountRegionResponse.accountRegion || "UNKNOWN");
          } catch (error) {
            console.error("Error fetching profile account region");
            setAccountRegion("UNKNOWN");
          }
          setProfileComponentsLoaded(true);
        } catch (error) {
          console.error("Error fetching profile components");
          setProfileComponents([]);
          setAccountRegion("UNKNOWN");
          setInitialRedirectUrl(decodedToken.failureUrl);
          setInitialRenderedComponent(CONFIRMATION)
          setProfileComponentsLoaded(true);
        }
      } else if (decodedToken.type === DOC) {
        setFlowType(DOC);
        setAuthorized(true);
        setAccessToken(access_token);
        setSessionToken(session_token);
      } else {
        setAuthorized(false);
      }

      setLoading(false);
    };

    initialize().then();
  }, [successRedirectUrl, failureRedirectUrl]);

  const renderAuthorizedContent = () => {
    if (flowType === SVS) {
      if (!profileComponentsLoaded) {
        return (
          <div style={{ paddingTop: '20px', paddingLeft: '47%' }}>
            <AriaAnnouncerProvider>
              <Spinner />
            </AriaAnnouncerProvider>
          </div>
        );
      }

      return <SvsFlow
        accessToken={accessToken}
        sessionToken={sessionToken}
        successRedirectUrl={successRedirectUrl}
        failureRedirectUrl={failureRedirectUrl}
        initialRedirectUrl={initialRedirectUrl}
        profileComponents={profileComponents}
        accountRegion={accountRegion}
        initialRenderedComponent={initialRenderedComponent}
        onProfileComponentsChange={setProfileComponents}
      />;
    } else if (flowType === DOC) {
      return <DocFlow accessToken={accessToken} sessionToken={sessionToken} />;
    }
    return null;
  };

  const renderUnauthorizedContent = () => (
    <ColumnLayout container style={{ minHeight: '100vh', justifyContent: 'center', alignItems: 'center' }}>
      <ColumnLayout item>
        {!loading ? (
          <Card>
            <div>
              <h1>{t('session_expired')}</h1>
              <span>{t('authentication_failed')}</span>
            </div>
          </Card>
        ) : null}
        {loading ? (
          <div style={{ paddingTop: '20px', paddingLeft: '47%' }}>
            <AriaAnnouncerProvider>
              <Spinner />
            </AriaAnnouncerProvider>
          </div>
        ) : null}
      </ColumnLayout>
    </ColumnLayout>
  );

  return (
    <ColumnLayout container className={root} direction="column">
      {authorized && accessToken && sessionToken && (flowType === SVS ? successRedirectUrl && failureRedirectUrl : true)
        ? renderAuthorizedContent() : renderUnauthorizedContent()}
    </ColumnLayout>
  );
};

export default withStyles(styles)(Landing);
